/**
 * Opt-in gesture sampler for before/after performance numbers.
 *
 * `perf-trace.ts` only reports stalls above fixed thresholds and only in dev
 * builds, which run React in development mode. This sampler records EVERY
 * animation-frame delta and every `recordPerfPhase` duration while a canvas
 * gesture (viewport move or frame drag) is active, and writes one summary
 * line per gesture:
 *
 *   [perf-sample] pan raf p50=8.3 p95=16.9 max=41.2 n=1180; phases native-collect p95=1.4, viewport-change p95=0.6
 *
 * Enabled by `localStorage["denker.perf.sample"] = "1"` or
 * `VITE_DENKER_PERF_SAMPLE=1` at build time. Read once at startup; when off
 * nothing is scheduled and every entry point returns on one boolean check.
 * Lines go to the console, which dev builds forward to the dev log.
 */

const SAMPLE_FLAG_KEY = "denker.perf.sample";
const RAF_CAPACITY = 4096;
const PHASE_CAPACITY = 1024;
const ZOOM_CHANGE_EPSILON = 1e-3;

type PerfGestureKind = "viewport" | "drag";

type SampleRing = {
  push: (value: number) => void;
  values: () => number[];
  clear: () => void;
};

/** Fixed-size ring buffer: keeps the most recent `capacity` samples. */
export function createSampleRing(capacity: number): SampleRing {
  const buffer = new Float64Array(capacity);
  let next = 0;
  let size = 0;
  return {
    push: (value) => {
      buffer[next] = value;
      next = (next + 1) % capacity;
      size = Math.min(size + 1, capacity);
    },
    values: () => {
      const start = size < capacity ? 0 : next;
      return Array.from({ length: size }, (_, index) => {
        return buffer[(start + index) % capacity] ?? 0;
      });
    },
    clear: () => {
      next = 0;
      size = 0;
    },
  };
}

/** Nearest-rank percentile of an ascending-sorted series. */
export function percentile(
  sorted: readonly number[],
  fraction: number,
): number {
  if (sorted.length === 0) return 0;
  const rank = Math.ceil(fraction * sorted.length);
  return sorted[Math.min(sorted.length, Math.max(1, rank)) - 1] ?? 0;
}

type PerfSamplerDeps = {
  requestFrame: (callback: FrameRequestCallback) => number;
  cancelFrame: (id: number) => void;
  log: (line: string) => void;
};

type PerfSampler = {
  begin: (kind: PerfGestureKind, zoom?: number) => void;
  end: (kind: PerfGestureKind, zoom?: number) => void;
  phase: (name: string, duration: number) => void;
};

const formatMs = (value: number) => value.toFixed(1);

export function createPerfSampler(
  enabled: boolean,
  deps: PerfSamplerDeps,
): PerfSampler {
  const rafDeltas = createSampleRing(RAF_CAPACITY);
  const phases = new Map<string, SampleRing>();
  let active: { kind: PerfGestureKind; zoom?: number } | null = null;
  let frameId: number | null = null;
  let lastFrameAt: number | null = null;

  const tick = (now: number) => {
    if (lastFrameAt !== null) rafDeltas.push(now - lastFrameAt);
    lastFrameAt = now;
    frameId = deps.requestFrame(tick);
  };

  const summarize = (label: string) => {
    const raf = rafDeltas.values().sort((a, b) => a - b);
    if (raf.length === 0) return;
    const phaseParts = [...phases.entries()]
      .map(
        ([name, ring]) => [name, ring.values().sort((a, b) => a - b)] as const,
      )
      .filter(([, values]) => values.length > 0)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(
        ([name, values]) => `${name} p95=${formatMs(percentile(values, 0.95))}`,
      );
    deps.log(
      `[perf-sample] ${label} raf p50=${formatMs(percentile(raf, 0.5))} p95=${formatMs(percentile(raf, 0.95))} max=${formatMs(raf[raf.length - 1] ?? 0)} n=${raf.length}; phases ${phaseParts.length > 0 ? phaseParts.join(", ") : "none"}`,
    );
  };

  return {
    begin: (kind, zoom) => {
      if (!enabled || active) return;
      active = { kind, zoom };
      rafDeltas.clear();
      for (const ring of phases.values()) ring.clear();
      lastFrameAt = null;
      frameId = deps.requestFrame(tick);
    },
    end: (kind, zoom) => {
      if (!active || active.kind !== kind) return;
      const startZoom = active.zoom;
      active = null;
      if (frameId !== null) deps.cancelFrame(frameId);
      frameId = null;
      const zoomed =
        startZoom !== undefined &&
        zoom !== undefined &&
        Math.abs(zoom - startZoom) > ZOOM_CHANGE_EPSILON;
      summarize(kind === "drag" ? "drag" : zoomed ? "zoom" : "pan");
    },
    phase: (name, duration) => {
      if (!active) return;
      let ring = phases.get(name);
      if (!ring) {
        ring = createSampleRing(PHASE_CAPACITY);
        phases.set(name, ring);
      }
      ring.push(duration);
    },
  };
}

const readSampleFlag = (): boolean => {
  if (import.meta.env.VITE_DENKER_PERF_SAMPLE === "1") return true;
  try {
    return window.localStorage.getItem(SAMPLE_FLAG_KEY) === "1";
  } catch {
    return false;
  }
};

const sampler = createPerfSampler(readSampleFlag(), {
  requestFrame: (callback) => window.requestAnimationFrame(callback),
  cancelFrame: (id) => window.cancelAnimationFrame(id),
  log: (line) => console.info(line),
});

export const beginPerfSample = sampler.begin;
export const endPerfSample = sampler.end;
export const samplePerfPhase = sampler.phase;
